// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MetricCard
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS metric card for a single computed summary
 * metric. The card formats percentages and raw counts while exposing trend
 * coloring from the shared analytics tokens.
 */

import { type MetricSummary, TREND_COLORS } from "@geenius/analytics";
import { Badge, Card, CardContent } from "@geenius/ui/solidjs";
import { type Component, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS metric card.
 *
 * @property metric Metric summary payload rendered by the card.
 * @property error Optional error value rendered instead of the metric body.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the card wrapper.
 */
export interface MetricCardProps {
  metric: MetricSummary;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS metric card for one summary metric.
 *
 * @param props Summary metric payload for the card.
 * @returns A metric card element with formatted value and optional trend.
 */
export const MetricCard: Component<MetricCardProps> = (props) => {
  const copy = createAnalyticsCopy();
  const trendVariant = () =>
    props.metric.trend === "up"
      ? "success"
      : props.metric.trend === "down"
        ? "danger"
        : "secondary";
  const fmt = (value: number): string =>
    props.metric.format === "percent"
      ? copy.formatPercent(value)
      : props.metric.format === "duration"
        ? copy.formatDuration(value)
        : copy.formatNumber(value);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="pt-6">
          <ErrorState
            error={props.error}
            title={props.metric.label}
            onRetry={props.onRetry}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent class="flex flex-col gap-3 pt-6">
        <span
          class="text-sm text-muted-foreground"
          data-analytics="metric-label"
        >
          {props.metric.label}
        </span>
        <span
          class="text-2xl font-semibold tracking-tight"
          data-analytics="metric-value"
        >
          {fmt(props.metric.value)}
        </span>
        <Show when={props.metric.change !== undefined}>
          <Badge variant={trendVariant()} size="sm">
            <span
              data-analytics="metric-change"
              data-trend={props.metric.trend}
              style={{ color: TREND_COLORS[props.metric.trend] }}
            >
              {props.metric.trend === "up"
                ? "↑"
                : props.metric.trend === "down"
                  ? "↓"
                  : "→"}{" "}
              {copy.formatPercent(props.metric.changePercent ?? 0)}
            </span>
          </Badge>
        </Show>
      </CardContent>
    </Card>
  );
};
